import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import { LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME, PRIORITY_PATHWAY_SLUG_COOKIE_NAME } from '~/const';
import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import { GET_PATHWAYS_CONTAINING_VIDEO } from '~/graphql/videos';
import useCaseStudies from '~/hooks/useCaseStudies';
import useEventListener from '~/hooks/useEventListener';
import { VerseEvents } from '~/hooks/useVerse';
import { CaseStudy, Pathway, Query, QueryGetPathwaysContainingVideoArgs, Video } from '~/types';
import Cookie from '~/utils/cookies';
import bindCaseStudiesToPathway, {
  IPathwayWithCaseStudy,
} from '~/utils/pathway/bindCaseStudiesToPathway';
import { sleep } from '~/utils/sleep';
import { toBoolean } from '~/utils/toBoolean';

const usePathways = (videoId) => {
  const withoutNavigation = useWithoutNavigation();
  const [priorityPathwaySlug, setPriorityPathwaySlug] = useState('');
  const [openedPathway, setOpenedPathway] = useState<IPathwayWithCaseStudy | null>(null);
  const [nextItem, setNextItem] = useState<Video | CaseStudy | null | undefined>(null);

  const location = useLocation();
  const { userInfo } = useUserInfoContext();
  const userId = userInfo?.id ? userInfo.id : '';
  const lastViewedPathwaySlug = Cookie.get(LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME);

  const {
    data: pathwaysData,
    loading: pathwaysDataLoading,
    called: pathwaysDataCalled,
    refetch: refetchPathwaysData,
  } = useQuery<Pick<Query, 'getPathwaysContainingVideo'>, QueryGetPathwaysContainingVideoArgs>(
    GET_PATHWAYS_CONTAINING_VIDEO,
    {
      variables: {
        filter: {
          video_id: videoId,
          user_id: userId,
          last_viewed_pathway_slug: lastViewedPathwaySlug,
          priority_pathway_slug: priorityPathwaySlug,
          include_test_publish_status: true,
        },
      },
      skip: !userId || !videoId,
    },
  );

  const pathwaysDataLoaded = pathwaysDataCalled && !pathwaysDataLoading;

  const pathways = withoutNavigation
    ? pathwaysData?.getPathwaysContainingVideo?.filter(
        (pathway) => pathway?.slug === lastViewedPathwaySlug,
      )
    : pathwaysData?.getPathwaysContainingVideo;
  const pathwaysIds = pathways?.map((pathway) => Number(pathway?.id));

  const {
    caseStudiesByPathwayIdMap,
    loaded: caseStudyLoaded,
    loading: loadingCaseStudy,
  } = useCaseStudies(pathwaysIds || []);

  const pathwaysWithCaseStudy = useMemo(
    () =>
      pathways
        ?.filter(toBoolean<Pathway>)
        .map(bindCaseStudiesToPathway(caseStudiesByPathwayIdMap)) || [],
    [pathways, caseStudiesByPathwayIdMap],
  );

  const isLastVideo =
    Array.isArray(openedPathway?.videos) &&
    openedPathway?.videos[openedPathway?.videos?.length - 1]?.id === videoId;
  const availableQuiz = openedPathway?.available_quizes?.[0];
  const nextItemIndexInPathway =
    openedPathway && openedPathway?.items?.length
      ? openedPathway.items.findIndex((item) => item?.id === videoId) + 1
      : undefined;
  const videoIdsFromAllPathways =
    pathways
      ?.flatMap((pathway) => pathway?.videos?.map((video) => video?.id))
      ?.filter(toBoolean<string>) || [];

  const handleTogglePathway = useCallback(
    (pathway: IPathwayWithCaseStudy) =>
      setOpenedPathway((old) => (old === pathway ? null : pathway)),
    [setOpenedPathway],
  );

  useEffect(() => {
    setPriorityPathwaySlug(Cookie.get(PRIORITY_PATHWAY_SLUG_COOKIE_NAME) || '');
    Cookie.set(PRIORITY_PATHWAY_SLUG_COOKIE_NAME, '');
  }, [location]);

  //calculate next video in pathway
  useEffect(() => {
    if (nextItemIndexInPathway) {
      const isLastVideoInPathway = nextItemIndexInPathway === openedPathway?.items?.length;
      if (!isLastVideoInPathway) {
        setNextItem(openedPathway?.items?.[nextItemIndexInPathway]);
      } else {
        setNextItem(null);
      }
    }
  }, [nextItemIndexInPathway, openedPathway]);

  useEventListener(VerseEvents.VideoCompleted, async () => {
    if (!isLastVideo) {
      return;
    }

    let success = false;
    let retry = 5;
    const currentAmountOfAvailableQuizes = openedPathway?.available_quizes?.length || 0;
    const currentAmountOfQuizes = openedPathway?.quizes?.length || 0;

    while (!success && retry > 0) {
      retry--;
      const { data: pathwaysData } = await refetchPathwaysData();

      const refetchedOpenedPathway = pathwaysData?.getPathwaysContainingVideo?.find(
        (item) => item?.id === openedPathway?.id,
      );

      if (!refetchedOpenedPathway) {
        await sleep(600);
        continue;
      }

      const refetchedAmountOfAvailableQuizes =
        refetchedOpenedPathway?.available_quizes?.length || 0;

      if (
        refetchedAmountOfAvailableQuizes > currentAmountOfAvailableQuizes ||
        refetchedAmountOfAvailableQuizes === currentAmountOfQuizes
      ) {
        success = true;

        setOpenedPathway(
          bindCaseStudiesToPathway(caseStudiesByPathwayIdMap)(refetchedOpenedPathway),
        );
      } else {
        await sleep(600);
      }
    }
  });

  return {
    pathways: pathwaysWithCaseStudy,
    availableQuiz,
    videoIdsFromAllPathways,
    handleOpenPathway: setOpenedPathway,
    handleTogglePathway,
    nextItem,
    isLastVideo,
    openedPathway,
    pathwaysLoading: pathwaysDataLoading || loadingCaseStudy,
    pathwaysLoaded: pathwaysDataLoaded && caseStudyLoaded,
  };
};

export default usePathways;
